<script setup lang="ts">
import { computed } from 'vue'

type Variant = 'primary' | 'secondary' | 'ghost' | 'dark' | 'peach'
type Size = 'sm' | 'md' | 'lg'

const props = withDefaults(
  defineProps<{
    variant?: Variant
    size?: Size
    as?: 'button' | 'a'
    href?: string
    type?: 'button' | 'submit' | 'reset'
    block?: boolean
    iconRight?: boolean
    ring?: boolean
    disabled?: boolean
    ariaLabel?: string
  }>(),
  { variant: 'primary', size: 'md', as: 'button', type: 'button', block: false, iconRight: true, ring: false, disabled: false }
)

const tag = computed(() => (props.as === 'a' ? 'a' : 'button'))
const classes = computed(() => [
  'btn',
  `btn--${props.variant}`,
  `btn--${props.size}`,
  props.block && 'btn--block',
  props.ring && 'btn--ring',
])
</script>

<template>
  <component
    :is="tag"
    :class="classes"
    :type="as === 'button' ? type : undefined"
    :href="as === 'a' ? href : undefined"
    :disabled="as === 'button' ? disabled : undefined"
    :aria-disabled="as === 'a' && disabled ? 'true' : undefined"
    :aria-label="ariaLabel"
  >
    <span class="btn__label"><slot /></span>
    <svg v-if="iconRight" class="btn__icon" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M4 10h11M10 5l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  </component>
</template>

<style scoped>
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: var(--font-body);
  font-weight: 600;
  border-radius: var(--radius-pill);
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    background-color var(--dur-base) var(--ease-out),
    color var(--dur-base) var(--ease-out),
    transform var(--dur-base) var(--ease-spring),
    box-shadow var(--dur-base) var(--ease-out);
  text-decoration: none;
  line-height: 1;
  white-space: nowrap;
}
.btn:hover { transform: translateY(-1px); }
.btn:active { transform: translateY(0); }

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}
.btn:disabled:hover { transform: none; }
.btn:disabled .btn__icon { transform: none; }
.btn--ring:disabled::before { opacity: 0; }

.btn--sm { padding: 0.625rem 1rem; font-size: var(--text-sm); }
.btn--md { padding: 0.875rem 1.375rem; font-size: var(--text-base); }
.btn--lg { padding: 1.05rem 1.75rem; font-size: var(--text-lg); }

.btn--primary {
  background: var(--primary);
  color: var(--text-on-primary);
  box-shadow: var(--shadow-sm);
}
.btn--primary:hover { background: var(--primary-hover); box-shadow: var(--shadow-md); }

.btn--secondary {
  background: var(--color-white);
  color: var(--color-chocolate);
  border-color: var(--border-strong);
}
.btn--secondary:hover { background: var(--bg-soft); }

.btn--ghost {
  background: transparent;
  color: var(--color-chocolate);
}
.btn--ghost:hover { background: color-mix(in oklab, var(--color-chocolate) 6%, white); }

.btn--dark {
  background: var(--color-chocolate);
  color: var(--color-white);
}
.btn--dark:hover { background: color-mix(in oklab, var(--color-chocolate) 85%, white); }

.btn--peach {
  background: var(--color-peach);
  color: var(--color-chocolate);
}
.btn--peach:hover { background: color-mix(in oklab, var(--color-peach) 80%, white); }

.btn--block { display: flex; width: 100%; }

/* Anneau dégradé révélé au hover / focus (technique mask-composite) */
.btn--ring {
  isolation: isolate;
  position: relative;
}
.btn--ring::before {
  content: "";
  pointer-events: none;
  position: absolute;
  inset: -3px;
  padding: 3px;
  border-radius: inherit;
  background: linear-gradient(45deg,
    var(--color-lavander),
    color-mix(in oklab, var(--color-pink) 35%, white) 38%,
    var(--color-peach) 71%,
    var(--color-orange));
  opacity: 0;
  transform: scale(1.02, 1.075);
  transition:
    opacity var(--dur-fast) var(--ease-out),
    transform var(--dur-fast) var(--ease-out);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
          mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
          mask-composite: exclude;
}
.btn--ring:hover::before,
.btn--ring:focus-visible::before {
  opacity: 1;
  transform: scale(1);
}

.btn__icon {
  width: 1.1em;
  height: 1.1em;
  transition: transform var(--dur-base) var(--ease-out);
  flex-shrink: 0;
}
.btn:hover .btn__icon { transform: translateX(3px); }
</style>
